/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAutoRefresh } from "../BaseQuery";
import { ApiResponse } from "../../types/apiTypes";
import {  VipPerson } from "@/types/vipTypes";
import { initData, setData, setQuerySuccess } from "../reducers/searchSlice";
import { clearVips,  setVips } from "../reducers/vipSlice";

// (1) Define your search-result shape
type SearchResult = {
  source: VipPerson[];
  totalHits: number | undefined;
  page: number;
  limit: number;
};

export type VipPickerOption = Pick<VipPerson, "en_full_name" | "heb_full_name" | "donor_organization_name">;

export const vipApi = createApi({
  reducerPath: "vipApi",
  baseQuery: baseQueryWithAutoRefresh,
  tagTypes: ["Vip", "my-vip", "token", "Area"],
  endpoints: (builder) => ({
    getVipLabels: builder.query<ApiResponse<{ name: string }[]>, void>({
      query: () => ({ url: "/vip/interest-labels", method: "GET" }),
      providesTags: ["Area"],
    }),
    // —— create a new VIP (mutation invalidates the single-Vip tag) ——
    postNewVip: builder.mutation<ApiResponse<{ vip: any }>, VipPerson>({
      query: (newVip) => {
        return {
          url: "/admin/vip",
          method: "POST",
          body: { data: newVip },
        };
      },
      invalidatesTags: ["Vip"],
    }),

    deleteVip: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/vip/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Vip"],
    }),

    // —— fetch one VIP by id ——
    getVipById: builder.query<ApiResponse<VipPerson>, string>({
      providesTags: ["Vip", 'my-vip'],
      query: (id) => ({
        url: `/vip/${id}`,
        method: "GET",
      }),
      // mark this data under the "Vip" tag

      // after a successful GET, invalidate the "my-vip" list so it refetches
    }),

    // —— initial full-list search ——
    getAllVips: builder.query<ApiResponse<SearchResult>, void>({
      query: () => ({
        url: "/vip/search",
        method: "POST",
        body: { page: 1, query: "" },
      }),
      providesTags: ["Vip"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const resData = response.data;
          dispatch(
            initData({
              data: resData.source,
              totalHits: resData.totalHits || 0,
              page: resData.page,
              limit: resData.limit,
              searchTerm: "",
              type: "vip",
            })
          );
        } catch {
          dispatch(setData([]));
        }
      },
    }),

    // —— search (with term + paging + optional rank) ——
    searchVips: builder.mutation<ApiResponse<SearchResult>, { page: number; query: string;  }>({
      query: ({ query, page, }) => ({
        url: `/vip/search`,
        method: "POST",
        body: { query, page },
      }),
      async onQueryStarted({ query }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const resData = response.data;
          dispatch(setQuerySuccess(true));
          dispatch(
            initData({
              data: resData.source,
              totalHits: resData.totalHits || 0,
              page: resData.page,
              limit: resData.limit,
              searchTerm: query,
              type: "vip",
            })
          );
        } catch {
          dispatch(setQuerySuccess(false));
          dispatch(setData([]));
        }
      },
    }),

    // —— fetch next page (no slice dispatch) ——
    getNextVips: builder.mutation<ApiResponse<SearchResult>, { page: number; searchTerm: string; }>({
      query: ({ searchTerm, page }) => ({
        url: `/vip/search`,
        method: "POST",
        body: { query: searchTerm, page },
      }),
    }),
    getMyTokens: builder.query<ApiResponse<{ tokenAvail: number }[]>, void>({
      providesTags: ["token"],
      query: () => ({
        url: "/user/token",
        method: "GET",
      }),
    }),

    // —— fetch “my vips” list ——
    getMyVips: builder.query<ApiResponse<VipPerson[]>, void>({
      providesTags: ["my-vip"],
      query: () => ({
        url: "/vip/my-vips",
        method: "GET",
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(setVips(response.data));
        } catch {
          dispatch(clearVips());
        }
      },
    }),
    getVipCompletions: builder.query<ApiResponse<string[]>, string>({
      query: (query) => ({
        url: `/vip/completion?query=${query}`,
        method: "GET",
      }),
    }),
    searchVipsForPicker: builder.mutation<VipPickerOption[], string>({
      query: (query) => ({
        url: `/vip/search`,
        method: "POST",
        body: { query, page: 1 },
      }),
      transformResponse: (response: ApiResponse<SearchResult>) => {
        return response.data?.source?.filter((vip) => vip.en_full_name).map((vip) => ({
          en_full_name: vip.en_full_name,
          heb_full_name: vip.heb_full_name,
          donor_organization_name: vip.donor_organization_name,
        })) ?? [];
      },
    }),
    buyVip: builder.mutation<ApiResponse<{ vip: VipPerson }>, { vipId: string }>({
      invalidatesTags:['my-vip','token'],
      query: ({ vipId }) => ({
        url: `/vip/buy-vip/${vipId}`,
        method: "POST",
      })
    }),
  }),
});

export const {
  useGetVipLabelsQuery,
  useGetVipByIdQuery,
  useLazyGetVipByIdQuery,
  useGetMyVipsQuery,
  useLazyGetVipCompletionsQuery,
  useBuyVipMutation,
  useGetMyTokensQuery,
  useLazyGetAllVipsQuery,
  useGetAllVipsQuery,
  useDeleteVipMutation,
  useLazyGetMyVipsQuery,
  useSearchVipsMutation,
  useSearchVipsForPickerMutation,
  usePostNewVipMutation,
  useGetNextVipsMutation,
} = vipApi;
